import React, { useState } from "react";
import { toast } from "sonner";
import { PublicHeader, PublicFooter } from "@/components/ventrix/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plane, Ship, Truck, Calculator, ArrowRight, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

const MODE_OPTS = [
  { v: "air", label: "Air", Icon: Plane },
  { v: "sea", label: "Sea", Icon: Ship },
  { v: "road", label: "Road", Icon: Truck },
];

export default function Quote() {
  const [f, setF] = useState({
    origin: "", destination: "", mode: "air", weight_kg: 100,
    length_cm: 60, width_cm: 40, height_cm: 40, pieces: 1,
    cargo_type: "general", sea_container: "lcl",
  });
  const [stage, setStage] = useState("form");   // form | email | done
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");   // honeypot
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Step 1 -> 2: native `required` fields must be filled for submit to fire.
  const toEmail = (e) => { e.preventDefault(); setStage("email"); };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email so we can send the quote"); return; }
    setSending(true);
    try {
      const body = {
        ...f,
        weight_kg: Number(f.weight_kg) || 0,
        length_cm: Number(f.length_cm) || 0,
        width_cm: Number(f.width_cm) || 0,
        height_cm: Number(f.height_cm) || 0,
        pieces: Math.max(1, Number(f.pieces) || 1),
        email: email.trim(), website,
      };
      await api.post("/v1/quote-request", body);
      setStage("done");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your request. Please try again.");
    } finally { setSending(false); }
  };

  const reset = () => { setStage("form"); setEmail(""); setWebsite(""); };

  return (
    <div className="min-h-screen">
      <PublicHeader />
      <section className="vtx-container py-12 md:py-16">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="vtx-eyebrow">/ Request a quote</div>
            <h1 className="mt-3 font-black text-3xl md:text-5xl tracking-tighter text-[#0E2F76]">A tailored freight quote, by email.</h1>
            <p className="mt-4 text-[#475569] max-w-md">Tell us about your shipment and where it's going. Our team prices it against live lane rates and emails you a quote — usually within one business day.</p>
            <div className="mt-8 vtx-card p-5 bg-[#F4FEFF]">
              <div className="vtx-eyebrow">How it works</div>
              <ol className="mt-3 text-sm text-[#0f172a] space-y-2 leading-relaxed list-decimal pl-5">
                <li>Enter your shipment details.</li>
                <li>Add your email.</li>
                <li>Our ops team reviews and emails your quote (PDF included).</li>
              </ol>
            </div>
          </div>

          <div className="lg:col-span-3">
            {stage === "done" ? (
              <div className="vtx-card p-8 bg-white text-center" data-testid="quote-request-done">
                <CheckCircle2 size={44} className="mx-auto text-[#166534]" />
                <h2 className="mt-4 text-2xl font-black text-[#0E2F76] tracking-tight">Request received</h2>
                <p className="mt-2 text-[#475569] max-w-sm mx-auto">Thanks — our team is preparing your quote and will email it to <span className="font-semibold text-[#0E2F76]">{email}</span> shortly.</p>
                <button className="vtx-btn-ghost mt-6" onClick={reset} data-testid="quote-new-request">Request another quote</button>
              </div>
            ) : stage === "email" ? (
              <form onSubmit={submitRequest} className="vtx-card p-6 md:p-8 bg-white shadow-sm" data-testid="quote-email-step">
                <div className="flex items-center gap-2 mb-6"><Mail size={18} className="text-[#0E2F76]" /><div className="vtx-eyebrow">Where should we send it?</div></div>
                <div className="vtx-card p-4 bg-[#F4FEFF] text-sm text-[#334155] mb-5">
                  <span className="font-semibold text-[#0E2F76]">{f.origin} → {f.destination}</span> · {f.mode.toUpperCase()} · {f.pieces} pc · {f.weight_kg} kg
                </div>
                {/* Honeypot: hidden from users; a bot that fills it is dropped server-side. */}
                <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                <Field label="Your email">
                  <input type="email" className="vtx-input" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quote-request-email" required />
                </Field>
                <div className="mt-6 flex gap-3">
                  <button type="button" className="vtx-btn-ghost inline-flex items-center gap-1.5" onClick={() => setStage("form")} data-testid="quote-back"><ArrowLeft size={14} /> Back</button>
                  <button type="submit" disabled={sending} className="vtx-btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="quote-request-submit">
                    {sending ? "Sending…" : (<>Request quote <ArrowRight size={16} /></>)}
                  </button>
                </div>
                <p className="text-[11px] text-[#475569] mt-3">No account needed. We'll only use your email to send this quote.</p>
              </form>
            ) : (
              <form onSubmit={toEmail} className="vtx-card p-6 md:p-8 bg-white shadow-sm" data-testid="quote-form">
                <div className="flex items-center gap-2 mb-6"><Calculator size={18} className="text-[#0E2F76]" /><div className="vtx-eyebrow">Shipment details</div></div>
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {MODE_OPTS.map((m) => (
                    <button type="button" key={m.v} onClick={() => setF({ ...f, mode: m.v })} data-testid={`mode-${m.v}`}
                      className={`p-3 border transition-colors flex flex-col items-center gap-2 ${f.mode === m.v ? "bg-[#0E2F76] text-white border-[#0E2F76]" : "bg-white text-[#0E2F76] border-[#A9C0E0]/60 hover:border-[#0E2F76]"}`} style={{ borderRadius: 2 }}>
                      <m.Icon size={20} strokeWidth={2.2} />
                      <span className="text-xs font-bold uppercase tracking-wider">{m.label}</span>
                    </button>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Origin"><input className="vtx-input" placeholder="Mombasa, KE" value={f.origin} onChange={set("origin")} data-testid="q-origin" required /></Field>
                  <Field label="Destination"><input className="vtx-input" placeholder="Rotterdam, NL" value={f.destination} onChange={set("destination")} data-testid="q-destination" required /></Field>
                  <Field label="Pieces"><input type="number" min="1" className="vtx-input" value={f.pieces} onChange={set("pieces")} data-testid="q-pieces" /></Field>
                  <Field label="Weight per piece (kg)"><input type="number" min="0.1" step="0.1" className="vtx-input" value={f.weight_kg} onChange={set("weight_kg")} data-testid="q-weight" required /></Field>
                  <Field label="Length (cm)"><input type="number" min="0" className="vtx-input" value={f.length_cm} onChange={set("length_cm")} data-testid="q-length" /></Field>
                  <Field label="Width (cm)"><input type="number" min="0" className="vtx-input" value={f.width_cm} onChange={set("width_cm")} data-testid="q-width" /></Field>
                  <Field label="Height (cm)"><input type="number" min="0" className="vtx-input" value={f.height_cm} onChange={set("height_cm")} data-testid="q-height" /></Field>
                  <Field label="Cargo type">
                    <select className="vtx-input" value={f.cargo_type} onChange={set("cargo_type")} data-testid="q-cargo">
                      <option value="general">General cargo</option>
                      <option value="electronics">Electronics</option>
                      <option value="perishable">Perishable</option>
                      <option value="dangerous">Dangerous goods</option>
                      <option value="reefer">Reefer</option>
                    </select>
                  </Field>
                  {f.mode === "sea" && (
                    <Field label="Sea container">
                      <select className="vtx-input" value={f.sea_container} onChange={set("sea_container")} data-testid="q-container">
                        <option value="lcl">LCL — less than container</option>
                        <option value="fcl_20">FCL 20'</option>
                        <option value="fcl_40">FCL 40'</option>
                      </select>
                    </Field>
                  )}
                </div>
                <button type="submit" className="vtx-btn-primary mt-6 w-full inline-flex items-center justify-center gap-2" data-testid="quote-submit">
                  Request a quote <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="vtx-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
